import { PermissionType } from './permission';
import { Bookmark } from './bookmark';
import { Store } from '../store/store';

// ---------------------------------------------------------------------------
// Interface describing the subset of JobModel needed by the permission
// helpers, avoiding a direct import of JobModel and preventing circular
// imports.
// ---------------------------------------------------------------------------

export interface PermissionContext {
  readonly id: string;
  readonly userId: string;
  readonly store: Store;
   
  readonly userPermissions: readonly (string | undefined)[] | null | undefined;
}

// ---------------------------------------------------------------------------
// Permission check — standalone function
// ---------------------------------------------------------------------------

export function checkPermission(ctx: PermissionContext, type: PermissionType): boolean {
  switch (type) {
    case 'UserEditJob':
      return !!ctx.userPermissions?.includes('Edit');

    case 'DeleteJob':
      return !!ctx.userPermissions?.includes('Delete');

    case 'UserCopyJob':
      return !!ctx.store.user?.hasPermission('UserUpload');

    case 'UserViewJobMenu':
      return !!(ctx.userPermissions?.includes('Edit') || ctx.userPermissions?.includes('Delete'));

    case 'EditTrainerMoment':
      return !!ctx.userPermissions?.includes('EditTrainerMoment');

    case 'EditUserMoment':
      return !!ctx.userPermissions?.includes('EditUserMoment');

    case 'EditIsPublic':
      return !!ctx.userPermissions?.includes('EditIsPublic');

    case 'ChangeOwner':
      return !!ctx.store.user?.hasPermission('ChangeOwner');

    case 'AdminEditJob':
      return !!ctx.store.user?.roles.includes('Admin');

    case 'EmbedPlayerWidget':
      return true; // this.isPublic;

    case 'AddComment':
      return !!ctx.userPermissions?.includes('AddComment');

    case 'EditAnyComment':
      return false;
    case 'DeleteAnyComment':
      return ctx.store.user?.isAdmin ?? false;

    case 'TrainerEditJob':
      return !!ctx.store.user?.roles.includes('Trainer');

    case 'EditVideoDetails':
      return !!ctx.userPermissions?.includes('EditIsPublic');
  }

  console.warn(`PermissionType '${type}' is not valid for a Job entity.`);
  return false;
}

// ---------------------------------------------------------------------------
// Owner authentication check
// ---------------------------------------------------------------------------

export function isOwnerAuthenticated(userId: string, store: Store): boolean {
  return userId === store.user?.id;
}

// ---------------------------------------------------------------------------
// Bookmark relation helpers
// ---------------------------------------------------------------------------

export function relatedBookmarks(jobId: string, store: Store): Bookmark[] {
  return store.bookmarks
    .filter(bkm => bkm.jobId === jobId);
}

export function relatedMomentBookmarks(bookmarks: Bookmark[]): Bookmark[] {
  return bookmarks
    .filter(bkm => bkm.targetType === 'Moment');
}

export function relatedTopicMomentBookmarks(momentBookmarks: Bookmark[]): Bookmark[] {
  return momentBookmarks
    .filter(bkm => bkm.moment!.isTopic);
}

export function relatedGenericMomentBookmarks(momentBookmarks: Bookmark[]): Bookmark[] {
  return momentBookmarks
    .filter(bkm => bkm.moment!.isGeneric);
}
